import { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { LuCamera, LuTrash2, LuHand, LuX, LuImageOff } from "react-icons/lu";
import { api, errMsg } from "../lib/api";
import { useActiveFlat } from "../lib/active-flat";
import { useAuth } from "../lib/auth";
import type { Item, ItemCategory } from "../lib/types";
import { ITEM_CATEGORIES, ITEM_CATEGORY_LABEL } from "../lib/types";
import { prepareUpload, uploadToPresigned } from "../lib/image";
import { Avatar } from "../components/Avatar";
import {
  Button,
  CenteredSpinner,
  EmptyState,
  ErrorBanner,
  IconButton,
  Input,
  Select,
} from "../components/ui";
import { formatRelativeTime } from "../lib/format";
import { ITEMS_EVENT } from "../components/AppShell";

export function InventoryPage() {
  const { flat } = useActiveFlat();
  const auth = useAuth();
  const flatId = flat!.id;
  const meId = auth.status === "authed" ? auth.user.id : null;

  const [items, setItems] = useState<Item[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [newNote, setNewNote] = useState("");
  const [newCategory, setNewCategory] = useState<ItemCategory>("KITCHEN");
  const [mine, setMine] = useState(true);
  const [filter, setFilter] = useState<ItemCategory | "ALL">("ALL");

  const load = useCallback(async () => {
    setError(null);
    try {
      setItems(await api.get<Item[]>(`/item/by-flat/${flatId}`));
    } catch (err) {
      setError(errMsg(err, "Couldn't load the inventory."));
    }
  }, [flatId]);

  useEffect(() => {
    load();
  }, [load]);

  // Live updates: another flatmate changed the inventory (SSE → window event).
  useEffect(() => {
    const onChange = () => load();
    window.addEventListener(ITEMS_EVENT, onChange);
    return () => window.removeEventListener(ITEMS_EVENT, onChange);
  }, [load]);

  const patch = (updated: Item) =>
    setItems((prev) => prev?.map((i) => (i.id === updated.id ? updated : i)) ?? null);

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setAdding(true);
    try {
      const item = await api.post<Item>(`/item/by-flat/${flatId}`, {
        name,
        category: newCategory,
        note: newNote.trim() || undefined,
        bringerId: mine && meId ? meId : undefined,
      });
      setItems((prev) => (prev ? [item, ...prev] : [item]));
      setNewName("");
      setNewNote("");
    } catch (err) {
      setError(errMsg(err, "Couldn't add the item."));
    } finally {
      setAdding(false);
    }
  };

  const setBringer = async (item: Item, bringerId: string | null) => {
    try {
      patch(await api.patch<Item>(`/item/${item.id}`, { bringerId }));
    } catch (err) {
      setError(errMsg(err, "Couldn't update who's bringing it."));
    }
  };

  const remove = async (item: Item) => {
    const previous = items;
    setItems((prev) => prev?.filter((i) => i.id !== item.id) ?? null);
    try {
      await api.delete(`/item/${item.id}`);
    } catch (err) {
      setError(errMsg(err, "Couldn't delete."));
      setItems(previous);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Inventory</h1>
        <p className="text-sm text-slate-500">
          Who's bringing what — so we don't end up with six toasters.
        </p>
      </div>

      <ErrorBanner message={error} />

      <form onSubmit={add} className="space-y-2 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-2 sm:flex-row">
          <Input
            placeholder="Item (e.g. Toaster)"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            disabled={adding}
            autoFocus
          />
          <Select
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value as ItemCategory)}
            disabled={adding}
            className="sm:w-40"
          >
            {ITEM_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {ITEM_CATEGORY_LABEL[c]}
              </option>
            ))}
          </Select>
        </div>
        <Input
          placeholder="Note (optional)"
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          disabled={adding}
        />
        <div className="flex items-center justify-between">
          <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={mine}
              onChange={(e) => setMine(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-brand-600"
            />
            I'm bringing this
          </label>
          <Button type="submit" loading={adding} disabled={!newName.trim()}>
            Add
          </Button>
        </div>
      </form>

      {items && items.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {(["ALL", ...ITEM_CATEGORIES] as const).map((c) => {
            const count = c === "ALL" ? items.length : items.filter((i) => i.category === c).length;
            if (c !== "ALL" && count === 0) return null;
            const active = filter === c;
            return (
              <button
                key={c}
                onClick={() => setFilter(c)}
                className={`rounded-full px-3 py-1 text-xs font-medium transition ${
                  active
                    ? "bg-brand-600 text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {c === "ALL" ? "All" : ITEM_CATEGORY_LABEL[c]} ({count})
              </button>
            );
          })}
        </div>
      )}

      {items === null ? (
        <CenteredSpinner />
      ) : items.length === 0 ? (
        <EmptyState title="Nothing tracked yet" description="Add the first item above." />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence initial={false}>
            {items
              .filter((item) => filter === "ALL" || item.category === filter)
              .map((item) => (
              <ItemCard
                key={item.id}
                item={item}
                meId={meId}
                onPhoto={patch}
                onSetBringer={setBringer}
                onRemove={remove}
                onError={setError}
              />
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}

function ItemCard({
  item,
  meId,
  onPhoto,
  onSetBringer,
  onRemove,
  onError,
}: {
  item: Item;
  meId: string | null;
  onPhoto: (updated: Item) => void;
  onSetBringer: (item: Item, bringerId: string | null) => void;
  onRemove: (item: Item) => void;
  onError: (message: string) => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const mineToBring = meId != null && item.bringerId === meId;

  const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file) return;
    setProgress(0);
    try {
      const { blob, contentType } = await prepareUpload(file);
      const { uploadUrl, key } = await api.post<{ uploadUrl: string; key: string }>(
        `/item/${item.id}/photo/presign`,
        { contentType },
      );
      await uploadToPresigned(uploadUrl, blob, contentType, setProgress);
      onPhoto(await api.post<Item>(`/item/${item.id}/photo/commit`, { key }));
    } catch (err) {
      onError(errMsg(err, "Photo upload failed."));
    } finally {
      setProgress(null);
    }
  };

  const removePhoto = async () => {
    try {
      onPhoto(await api.delete<Item>(`/item/${item.id}/photo`));
    } catch (err) {
      onError(errMsg(err, "Couldn't remove the photo."));
    }
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.96 }}
      transition={{ duration: 0.2 }}
      className="flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
    >
      <div className="relative aspect-[4/3] bg-slate-100">
        {item.photoUrl ? (
          <img
            src={item.photoUrl}
            alt={item.name}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-1 text-slate-300">
            <LuImageOff className="h-8 w-8" />
            <span className="text-xs">No photo</span>
          </div>
        )}

        {progress !== null && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/50 text-white">
            <div className="h-1.5 w-2/3 overflow-hidden rounded-full bg-white/30">
              <div
                className="h-full bg-white transition-[width]"
                style={{ width: `${Math.round(progress * 100)}%` }}
              />
            </div>
            <span className="text-xs">Uploading… {Math.round(progress * 100)}%</span>
          </div>
        )}

        <span className="absolute left-2 top-2 rounded-full bg-black/55 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-white">
          {ITEM_CATEGORY_LABEL[item.category]}
        </span>

        <div className="absolute right-2 top-2 flex gap-1.5">
          <button
            onClick={() => fileRef.current?.click()}
            disabled={progress !== null}
            aria-label={item.photoUrl ? "Replace photo" : "Add photo"}
            className="grid h-8 w-8 place-items-center rounded-full bg-white/90 text-slate-700 shadow hover:bg-white disabled:opacity-50"
          >
            <LuCamera className="h-4 w-4" />
          </button>
          {item.photoUrl && progress === null && (
            <button
              onClick={removePhoto}
              aria-label="Remove photo"
              className="grid h-8 w-8 place-items-center rounded-full bg-white/90 text-red-600 shadow hover:bg-white"
            >
              <LuX className="h-4 w-4" />
            </button>
          )}
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={onPick}
        />
      </div>

      <div className="flex flex-1 flex-col gap-2 p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="truncate font-medium text-slate-900">{item.name}</div>
            {item.note && <div className="truncate text-xs text-slate-500">{item.note}</div>}
          </div>
          <IconButton
            icon={<LuTrash2 className="h-4 w-4" />}
            label="Delete item"
            onClick={() => onRemove(item)}
            variant="danger"
          />
        </div>

        <div className="mt-auto flex items-center justify-between gap-2 border-t border-slate-100 pt-2">
          {item.bringer ? (
            <div className="flex min-w-0 items-center gap-2">
              <Avatar name={item.bringer.name} colour={item.bringer.colour} size="xs" />
              <span className="truncate text-xs text-slate-600">
                {mineToBring ? "You're bringing this" : `${item.bringer.name ?? "Someone"} is bringing it`}
              </span>
            </div>
          ) : (
            <span className="text-xs text-amber-600">Nobody's bringing this yet</span>
          )}

          {mineToBring ? (
            <button
              onClick={() => onSetBringer(item, null)}
              className="shrink-0 text-xs font-medium text-slate-400 hover:text-slate-600"
            >
              Unclaim
            </button>
          ) : (
            !item.bringer &&
            meId && (
              <button
                onClick={() => onSetBringer(item, meId)}
                className="flex shrink-0 items-center gap-1 rounded-md bg-brand-50 px-2 py-1 text-xs font-medium text-brand-700 hover:bg-brand-100"
              >
                <LuHand className="h-3.5 w-3.5" /> I'll bring it
              </button>
            )
          )}
        </div>
      </div>
    </motion.div>
  );
}
